import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React, { useRef, useState } from 'react'
import { SearchIcon, WarningIcon } from '~/Assets/icons'
import { pxToRem } from '~/utils/pxToRem'

import { useSearch } from '../../../hooks/useSearch'

function SearchPages() {
  const { searchQuery, setSearchQuery } = useSearch()
  const [inputValue, setInputValue] = useState<string | null>(null)
  const [showError, setShowError] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const currentPath = router.asPath

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault()

    setShowError(false)

    if (inputRef.current) {
      setInputValue(inputRef.current.value)
    }

    if (currentPath.includes('search-results') && inputValue === searchQuery) {
      setShowError(true)

      return
    }

    if (inputValue) {
      setSearchQuery(inputValue)
    }
  }

  return (
    <Popover placement="bottom-end" arrowPadding={20}>
      <PopoverTrigger>
        <Button
          variant="icon"
          aria-label="Search"
          borderRadius="0"
          _expanded={{
            bg: 'primary.darkGreen.100',
          }}
          _focusVisible={{
            shadow: 'outline',
            outline: '2px solid white',
            outlineOffset: '2px',
          }}
        >
          <SearchIcon boxSize="21px" fill="white" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        bg="primary.darkGreen.100"
        border="none"
        _focusVisible={{
          shadow: 'none',
          outline: '2px solid white',
          outlineOffset: '-4px',
        }}
        aria-label="Search Popover"
      >
        <PopoverArrow bg="primary.darkGreen.100" />
        <PopoverBody p="10px" boxShadow="lg">
          <form onSubmit={handleSubmit}>
            <FormControl>
              <FormLabel hidden={true}>Search</FormLabel>
              <InputGroup
                display="flex"
                flexDirection="row"
                width="100%"
                justifyContent="center"
                alignItems="center"
                bg="white"
                border="1px solid"
                borderColor="primary.teal.100"
                color="teal.900"
                rounded="4px"
                boxShadow="custom"
                _groupFocus={{
                  boxShadow: 'light',
                  outline: '1px solid white',
                  outlineOffset: '-1px',
                }}
              >
                <Input
                  value={inputValue ?? ''}
                  onChange={handleInputChange}
                  color="primary.darkGreen.100"
                  _groupHover={{ borderColor: 'teal.300' }}
                  border="0"
                  type="search"
                  flexShrink="1"
                  pr={pxToRem(100)}
                  sx={{
                    '::-webkit-search-cancel-button': {
                      position: 'relative',
                      left: '5px',
                      top: '-2px',
                    },
                  }}
                  required
                  minLength={2}
                  ref={inputRef}
                />
                <InputRightElement width="auto">
                  <Button
                    variant="primarySmall"
                    py="14px"
                    px="18px"
                    mr="7px"
                    type="submit"
                  >
                    Search
                  </Button>
                </InputRightElement>
              </InputGroup>
            </FormControl>
            {showError && (
              <Box
                display="flex"
                alignItems="flex-start"
                pt="15px"
                gap="5px"
                aria-live="polite"
              >
                <WarningIcon
                  role="img"
                  width={pxToRem(20)}
                  height={pxToRem(20)}
                  sx={{ path: { stroke: 'primary.white.100' } }}
                  aria-label="Note"
                />
                <Text
                  variant="smallBook"
                  lineHeight={{ base: pxToRem(20) }}
                  color="primary.white.100"
                >
                  Please enter a new search term
                </Text>
              </Box>
            )}
          </form>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default SearchPages
